/**
 * Workspace hub · sketch-theme card layout
 *
 * Lists sessions in the workspace as cards (design/canvas-first-v5.html
 * `.demo-card` style). 2026-05-13 dogfood feedback · 接真后端 ·
 * 加 "+ 新建 session" 按钮 · 让 dogfood 可以建任意干净 canvas。
 *
 * Auth · token + workspace_id from sessionStorage (set on /login)。
 * Backend route · GET /api/sessions (list) + POST /api/sessions (create)。
 */

import { createFileRoute, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { fetchSessions, createSession, updateSessionReviewEnabled, type SessionRow } from '../lib/fetch-sessions';
import { backendUrl } from './workspace.$workspaceId.canvas.$sessionId';
import { Icon } from '../components/Icon';

export const Route = createFileRoute('/workspace/$workspaceId')({
  component: WorkspaceLayout,
});

/**
 * Layout that shows the hub card grid only at exact `/workspace/:wsId`,
 * and renders nested `<Outlet />` (canvas page) when the URL has a
 * deeper segment like `/workspace/:wsId/canvas/:sessionId`.
 *
 * Use `useRouterState({ select })` so we read a stable string · plain
 * `useMatches()` returns a new array each render and triggers infinite
 * setState loop in this layout.
 */
function WorkspaceLayout(): JSX.Element {
  const { workspaceId } = Route.useParams();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const exact = pathname === `/workspace/${workspaceId}`;
  if (!exact) {
    return <Outlet />;
  }
  return <WorkspaceHub />;
}

function readToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('aiw.spa.token');
}

function formatRelativeTime(ts: number, nowMs: number = Date.now()): string {
  const diff = nowMs - ts;
  if (diff < 0) return '刚刚';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s 前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min 前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h 前`;
  const day = Math.floor(hr / 24);
  return `${day}d 前`;
}

export function normalizeSessionTitle(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function workspaceHubSummary(sessions: SessionRow[] | null, token: string | null): {
  label: string;
  activeCount: number;
  reviewOnCount: number;
} {
  if (!token) {
    return { label: '需要登录', activeCount: 0, reviewOnCount: 0 };
  }
  if (sessions === null) {
    return { label: '正在同步', activeCount: 0, reviewOnCount: 0 };
  }
  const activeCount = sessions.filter((s) => s.status === 'active').length;
  const reviewOnCount = sessions.filter((s) => s.review_enabled !== 0).length;
  return {
    label: sessions.length === 0
      ? '还没有协作任务'
      : `${sessions.length} 个协作任务 · ${activeCount} 个进行中`,
    activeCount,
    reviewOnCount,
  };
}

function sessionStatusLabel(status: SessionRow['status']): string {
  if (status === 'active') return '进行中';
  if (status === 'paused') return '已暂停';
  return '已归档';
}

function WorkspaceHub(): JSX.Element {
  const { workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() => readToken());
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [reviewUpdating, setReviewUpdating] = useState<Record<string, boolean>>({});
  const summary = workspaceHubSummary(sessions, token);

  // re-read token after hydration · sessionStorage isn't SSR-readable
  useEffect(() => { setToken(readToken()); }, []);

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const rows = await fetchSessions(backendUrl({}), token);
      setSessions(rows);
      setError(null);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }, [token]);

  useEffect(() => { void reload(); }, [reload]);

  function openCreateForm(): void {
    setShowCreateForm(true);
    setError(null);
  }

  function cancelCreateForm(): void {
    if (creating) return;
    setShowCreateForm(false);
    setNewSessionTitle('');
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!token || creating) return;
    setCreating(true);
    setError(null);
    try {
      const row = await createSession(backendUrl({}), token, normalizeSessionTitle(newSessionTitle));
      // Navigate into the new canvas immediately
      navigate({
        to: '/workspace/$workspaceId/canvas/$sessionId',
        params: { workspaceId, sessionId: row.session_id },
      });
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setCreating(false);
    }
  }

  function enterSession(sessionId: string): void {
    navigate({
      to: '/workspace/$workspaceId/canvas/$sessionId',
      params: { workspaceId, sessionId },
    });
  }

  async function handleToggleReview(session: SessionRow): Promise<void> {
    if (!token || reviewUpdating[session.session_id]) return;
    const next = session.review_enabled === 0;
    setReviewUpdating((s) => ({ ...s, [session.session_id]: true }));
    setError(null);
    try {
      const res = await updateSessionReviewEnabled(backendUrl({}), token, session.session_id, next);
      setSessions((rows) => rows?.map((s) =>
        s.session_id === session.session_id ? { ...s, review_enabled: res.review_enabled } : s,
      ) ?? rows);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setReviewUpdating((s) => ({ ...s, [session.session_id]: false }));
    }
  }

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div style={headerStyle}>
          <div style={titleBlockStyle}>
            <span style={eyebrowStyle}>工作区</span>
            <h1 style={titleStyle}>协作工作台</h1>
            <div style={metaStyle}>{summary.label}</div>
          </div>
          <code style={codeStyle}>{workspaceId}</code>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => navigate({ to: '/workspace/$workspaceId/agents', params: { workspaceId } })}
            style={secondaryBtnStyle()}
            data-testid="ws-agents-link"
          >
            <span style={buttonContentStyle}>
              <Icon name="settings" size={15} />
              Agent
            </span>
          </button>
          <button
            type="button"
            onClick={openCreateForm}
            disabled={!token || creating || showCreateForm}
            style={primaryBtnStyle(!token || creating || showCreateForm)}
            data-testid="ws-new-session"
          >
            <span style={buttonContentStyle}>
              <Icon name="plus" size={15} />
              新建任务
            </span>
          </button>
        </div>

        <div className="workspace-metric-grid" style={metricGridStyle} data-testid="ws-summary">
          <Metric label="任务" value={sessions === null ? '…' : String(sessions.length)} />
          <Metric label="进行中" value={String(summary.activeCount)} />
          <Metric label="审查开启" value={String(summary.reviewOnCount)} />
        </div>
      </div>

      <div style={noticeStyle}>
        {!token
          ? '未登录 · 请先到 /login'
          : sessions === null
            ? '加载中…'
            : sessions.length === 0
              ? '创建第一个协作任务后会直接进入画布'
              : '选择一个任务继续，或新建一个干净的协作画布'}
      </div>

      {error && (
        <div style={errorStyle} data-testid="ws-error">
          {error}
        </div>
      )}

      {showCreateForm && (
        <form style={createFormStyle} onSubmit={handleCreate} data-testid="ws-create-session-form">
          <label style={createLabelStyle}>
            <span style={createLabelTextStyle}>Session 名称</span>
            <input
              type="text"
              value={newSessionTitle}
              onChange={(e) => setNewSessionTitle(e.target.value)}
              disabled={creating}
              placeholder="例如：五大商社调研"
              autoFocus
              style={createInputStyle}
              data-testid="ws-create-session-title"
            />
          </label>
          <div style={createActionsStyle}>
            <button
              type="submit"
              disabled={!token || creating}
              style={primaryBtnStyle(!token || creating)}
              data-testid="ws-create-session-submit"
            >
              {creating ? '创建中…' : '创建并进入'}
            </button>
            <button
              type="button"
              onClick={cancelCreateForm}
              disabled={creating}
              style={secondaryBtnStyle()}
              data-testid="ws-create-session-cancel"
            >
              取消
            </button>
          </div>
        </form>
      )}

      {sessions !== null && sessions.length > 0 && (
        <div style={gridStyle}>
          {sessions.map((s) => (
            <div
              key={s.session_id}
              className="workspace-session-card"
              style={cardStyle}
              data-testid={`ws-session-card-${s.session_id}`}
            >
              <div style={cardHeadStyle}>
                <span style={cardSessionIdStyle}>#{s.session_id.slice(0, 8)}</span>
                <span style={statusChipStyle(s.status)}>{sessionStatusLabel(s.status)}</span>
              </div>
              <div style={cardBodyStyle}>
                <div style={cardTitleStyle}>{s.title ?? '未命名任务'}</div>
                <div style={cardHintStyle}>创建于 {formatRelativeTime(s.created_at)} · 审查 {s.review_enabled === 0 ? '关闭' : '开启'}</div>
              </div>
              <div style={cardFootStyle}>
                <button
                  type="button"
                  className="workspace-session-open"
                  style={cardOpenBtnStyle}
                  data-testid={`ws-session-open-${s.session_id}`}
                  onClick={() => enterSession(s.session_id)}
                >
                  打开任务板
                  <Icon name="arrow-right" size={14} />
                </button>
                <button
                  type="button"
                  className="workspace-session-review"
                  style={reviewToggleStyle(s.review_enabled !== 0)}
                  disabled={!token || reviewUpdating[s.session_id]}
                  data-testid={`ws-session-review-toggle-${s.session_id}`}
                  onClick={() => { void handleToggleReview(s); }}
                >
                  {reviewUpdating[s.session_id]
                    ? '更新中…'
                    : s.review_enabled === 0
                      ? '审查关'
                      : '审查开'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div style={metricStyle}>
      <span style={metricLabelStyle}>{label}</span>
      <strong style={metricValueStyle}>{value}</strong>
    </div>
  );
}

// === Styles · sketch theme cards ===

const pageStyle: CSSProperties = {
  maxWidth: 1180,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-8)',
};

const heroStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-6)',
  padding: 'var(--space-8)',
  border: '1.5px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-card)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-6)',
  flexWrap: 'wrap',
};

const titleBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 240,
};

const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 800,
  color: 'var(--fg-faint)',
  letterSpacing: 0,
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 30,
  fontWeight: 700,
  color: 'var(--fg-default)',
  margin: 0,
};

const codeStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  color: 'var(--fg-soft)',
  background: 'var(--bg-muted)',
  padding: '2px 8px',
  borderRadius: 'var(--radius-xs)',
};

const metaStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--fg-soft)',
};

const noticeStyle: CSSProperties = {
  ...metaStyle,
  padding: '0 2px',
};

const metricGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))',
  gap: 'var(--space-4)',
};

const metricStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
  padding: '8px 10px',
  border: '1px solid var(--border-soft)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-base)',
};

const metricLabelStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--fg-faint)',
};

const metricValueStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 18,
  color: 'var(--fg-default)',
};

const errorStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--accent-danger)',
  background: 'var(--bg-muted)',
  border: '1px solid var(--accent-danger)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 12px',
};

const createFormStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 'var(--space-6)',
  alignItems: 'stretch',
  padding: 'var(--space-6)',
  border: '2px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-card)',
};

const createLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const createLabelTextStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--fg-soft)',
};

const createInputStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  padding: '7px 10px',
  border: '1.5px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg-default)',
  minWidth: 0,
};

const createActionsStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-4)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const buttonContentStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: 'var(--space-8)',
};

function primaryBtnStyle(disabled: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    fontWeight: 600,
    padding: '6px 14px',
    border: '2px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    background: disabled ? 'var(--bg-muted)' : 'var(--accent-primary)',
    color: disabled ? 'var(--fg-faint)' : 'var(--accent-primary-fg)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : 'var(--shadow-card)',
  };
}

function secondaryBtnStyle(): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    padding: '6px 12px',
    border: '1.5px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-surface)',
    color: 'var(--fg-default)',
    cursor: 'pointer',
  };
}

const cardStyle: CSSProperties = {
  textAlign: 'left',
  padding: 0,
  background: 'var(--bg-surface)',
  border: '2px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};

const cardHeadStyle: CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1.5px dashed var(--border-soft)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'var(--bg-subtle)',
};

const cardSessionIdStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  color: 'var(--fg-soft)',
};

function statusChipStyle(status: 'active' | 'paused' | 'archived'): CSSProperties {
  const colorMap = {
    active: { bg: 'var(--accent-success-bg)', fg: 'var(--accent-success)', border: 'var(--accent-success)' },
    paused: { bg: 'var(--bg-muted)', fg: 'var(--accent-warning)', border: 'var(--accent-warning)' },
    archived: { bg: 'var(--bg-muted)', fg: 'var(--fg-faint)', border: 'var(--border-soft)' },
  } as const;
  const c = colorMap[status] ?? colorMap.active;
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    fontWeight: 600,
    padding: '1px 8px',
    borderRadius: 'var(--radius-pill)',
    border: `1.2px solid ${c.border}`,
    background: c.bg,
    color: c.fg,
    letterSpacing: 0,
  };
}

const cardBodyStyle: CSSProperties = {
  padding: 'var(--space-7) var(--space-6)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const cardTitleStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontSize: 18,
  fontWeight: 600,
  color: 'var(--fg-default)',
  lineHeight: 1.2,
};

const cardHintStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--fg-soft)',
};

const cardFootStyle: CSSProperties = {
  padding: '8px 12px',
  borderTop: '1.5px dashed var(--border-soft)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--accent-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-4)',
};

const cardOpenBtnStyle: CSSProperties = {
  ...buttonContentStyle,
  border: 0,
  padding: 0,
  background: 'transparent',
  color: 'var(--accent-primary)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

function reviewToggleStyle(enabled: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--border-default)',
    background: enabled ? 'var(--accent-success)' : 'var(--bg-muted)',
    color: enabled ? '#fff' : 'var(--fg-soft)',
    cursor: 'pointer',
  };
}
